import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, map, catchError, tap, of, BehaviorSubject, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IxfiResearchApi } from '../constants/enum.constants';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { StateKey, TransferState, makeStateKey } from '@angular/platform-browser';
import { HistoricalDataPayload } from '../models/payload.model';
import { CryptoNews, HistoricalData } from '../models/http.model';

interface ApiDetail {
    url: string;
    method: 'GET' | 'POST';
}

interface IxfiApiResponse<T> {
    data: T;
    status: number;
    message: string;
}

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    private http = inject(HttpClient);
    private platformId = inject(PLATFORM_ID);
    private transferState = inject(TransferState);

    private readonly exchangeBaseUrl: string = environment.IXFI_API;
    private readonly researchBaseUrl: string = environment.COIN_INFO_API;
    private _newsData = new BehaviorSubject<any | null>(null);
    newsObs = this._newsData.asObservable();

    private readonly API_MAP: Map<IxfiResearchApi, ApiDetail> = new Map<IxfiResearchApi, ApiDetail>([
        ['CRYPTO_NEWS_DETAIL', { url: `${this.researchBaseUrl}news/get-news-by-slug`, method: 'GET' }],
        ['CRYPTO_NEWS', { url: `${this.researchBaseUrl}news/get-new-by-coins`, method: 'POST' }],
        ['NEWS_PUBLISHERS', { url: `${this.researchBaseUrl}news/get-publishers-list`, method: 'GET' }],
        ['MARQUEE_COINS', { url: `${this.researchBaseUrl}coin-info/get-marquee-coins`, method: 'GET' }],
        ['TRENDING_COINS', { url: `${this.researchBaseUrl}coin-info/get-trending-coins`, method: 'GET' }],
        ['ALL_IXFI_COINS', { url: `${this.researchBaseUrl}coin-info/get-coin-info-list`, method: 'POST' }],
        ['COIN_CATEGORIES', { url: `${this.researchBaseUrl}coin-info/get-coin-categories`, method: 'GET' }],
        ['HISTORICAL_DATA', { url: `${this.researchBaseUrl}coin-info/get-coin-price-history`, method: 'POST' }],

        ['COIN_DETAIL', { url: `${this.researchBaseUrl}coin-info/get-coin-details`, method: 'POST' }],
        ['COIN_INFO_JSON', { url: `${this.exchangeBaseUrl}landing-page/coin-info-id`, method: 'GET' }],
        ['COIN_PRICE_JSON', { url: `${this.exchangeBaseUrl}api/public-api/coin-price-id`, method: 'GET' }],
        ['MARKET_PAIRS', { url: `${this.exchangeBaseUrl}coin-pairs/market-pairs-v2`, method: 'POST' }],
        ['WALLET_PAIRS', { url: `${this.exchangeBaseUrl}api/public-api/get-wallets-pairs`, method: 'POST' }],
        ['COIN_PAIRS', { url: `${this.exchangeBaseUrl}coin-pairs/pairs`, method: 'POST' }]
    ]);

    private apiCacheMap: Map<string, any> = new Map<string, any>();

    private isFirstRun: boolean = true;
    private formatErrors(error: any) {
        return throwError(() => new Error(error.error));
    }
    fetch<T>(apiKey: IxfiResearchApi, payload?: any, uniqueKey?: string): Observable<T> {
        // const stateKey: string = uniqueKey ? `${apiKey}_STK_${uniqueKey}` : `${apiKey}_STK`;
        // const stateTransferKey = makeStateKey<T>(stateKey);
        // const dataFromTransferState: T | null = this.getDataFromTransferState<T>(stateTransferKey);
        // if (!dataFromTransferState) {
        //     const dataFromCacheMap: T | null = this.getDataFromCacheMap<T>(stateTransferKey);
        //     if (!dataFromCacheMap)
        //         return this.httpCall<T>(apiKey, payload).pipe(
        //             tap(response => this.cacheApiResponse<T>(stateTransferKey, response))
        //         );

        //     return of(dataFromCacheMap);
        // }
        // return of(dataFromTransferState);
        return this.httpCall(apiKey, payload);
    }

    /**
     * Initiates HTTP request
     * @param apiKey
     * @param payload
     * @returns Observable of HTTP request
     */
    private httpCall<T>(apiKey: IxfiResearchApi, payload?: any): Observable<T> {
        const apiDetail: ApiDetail = this.API_MAP.get(apiKey)!;
        const urlEndPoint: string = apiDetail.url;
        let httpObs$: Observable<IxfiApiResponse<T>>;
        if (apiDetail.method === 'GET') httpObs$ = this.http.get<IxfiApiResponse<T>>(urlEndPoint);
        else httpObs$ = this.http.post<IxfiApiResponse<T>>(urlEndPoint, payload);

        return httpObs$.pipe(map(response => this.mapApiResponse<T>(response)));
    }

    private mapApiResponse<T>(response: IxfiApiResponse<T>): T {
        if (response.status === 200) return response.data;
        else throw new Error(`API responded with status code ${response.status}`);
    }

    /**
     * Retrieves data stored in Transfer State
     * This happens when Api call happens on the server and the data is hydrated onto the client
     * @param stateKey
     * @returns
     */
    private getDataFromTransferState<T>(stateKey: StateKey<T>): T | null {
        if (this.transferState.hasKey(stateKey)) {
            const dataFromServerState: T = this.transferState.get(stateKey, <T>[]);
            return dataFromServerState;
        }
        return null;
    }

    /**
     * Retrieves data stored in the Cache Map
     * This happens when Api call happens on the browser
     * @param apiKey
     * @returns
     */
    private getDataFromCacheMap<T>(stateKey: StateKey<T>): T | null {
        if (this.apiCacheMap.has(stateKey)) return this.apiCacheMap.get(stateKey);
        return null;
    }

    private cacheApiResponse<T>(stateKey: StateKey<T>, data: T) {
        if (isPlatformServer(this.platformId)) {
            this.logger<T>(stateKey);
            this.transferState.set(stateKey, data);
        }
        if (isPlatformBrowser(this.platformId)) this.apiCacheMap.set(stateKey, data);
    }

    private logger<T>(stateKey: StateKey<T>) {
        // console.log(`HTTP Call in server: ${stateKey}`);
    }

    fetchCryptoNews(
        pageNumber: number,
        payload: any
    ): Observable<{ docs: CryptoNews[]; totalDocs: number; docsCount: number }> {
        const apiKey = 'CRYPTO_NEWS';
        const stateKey = makeStateKey<{ docs: CryptoNews[]; totalDocs: number }>(apiKey);
        if (this.isFirstRun) {
            this.isFirstRun = false;
            if (this.transferState.hasKey(stateKey)) {
                const hydratedNews = this.transferState.get(stateKey, null);

                if (hydratedNews && hydratedNews.docs) {
                    return of({
                        docs: hydratedNews.docs,
                        totalDocs: hydratedNews.totalDocs,
                        docsCount: hydratedNews.docsCount
                    });
                }
            }
        }

        const urlEndPoint = this.API_MAP.get('CRYPTO_NEWS')!.url; // api url
        return this.http.post<any>(`${urlEndPoint}/${pageNumber}`, payload).pipe(
            map(response => {
                if (response.status === 200) {
                    if (response.message === 'No data found') {
                        return {
                            docs: [],
                            totalDocs: 0,
                            docsCount: 0
                        };
                    } else if (response.data && Array.isArray(response.data.docs)) {
                        return {
                            docs: response.data.docs,
                            totalDocs: response.data.totalDocs,
                            docsCount: response.data.docsCount
                        };
                    } else {
                        throw new Error('API response format error');
                    }
                } else {
                    throw new Error(`API responded with status code ${response.status}`);
                }
            }),
            tap(response => {
                // Update BehaviorSubject with the new response
                this._newsData.next(response);
                if (isPlatformServer(this.platformId)) {
                    console.log('Logging stateKey:', stateKey);
                }
                if (response) {
                    this.transferState.set(stateKey, response);
                }
            }),
            catchError(error => {
                console.error('API Error:', error);
                throw new Error(`API Error: ${error.message}`);
            })
        );
    }
    fetchCryptoNewsBySlug(slug: string): Observable<CryptoNews> {
        const urlEndPoint: string = this.API_MAP.get('CRYPTO_NEWS_DETAIL')!.url;
        return this.http
            .get<{
                message: string;
                status: number;
                data: CryptoNews;
            }>(`${urlEndPoint}/${slug}`)
            .pipe(map(response => response.data));
    }

    fetchCryptoNewsPublishers(): Observable<string[]> {
        const urlEndPoint: string = this.API_MAP.get('NEWS_PUBLISHERS')!.url;
        return this.http
            .get<{
                message: string;
                status: number;
                data: string[];
            }>(`${urlEndPoint}`)
            .pipe(map(response => response.data));
    }

    fetchHistoricalData(payload: HistoricalDataPayload): Observable<HistoricalData[]> {
        const urlEndPoint: string = this.API_MAP.get('HISTORICAL_DATA')!.url;
        return this.http
            .post<{
                status: number;
                message: string;
                data: { coin: string; data: HistoricalData[] };
            }>(urlEndPoint, payload)
            .pipe(
                map(response => {
                    let historicalData: HistoricalData[] = [];
                    if (response.status === 200) historicalData = response.data.data;
                    return historicalData;
                })
            );
    }

    get(path: string, params: HttpParams = new HttpParams()): Observable<any> {
        return this.http.get(`${environment.IXFI_API}${path}`, { params }).pipe(catchError(this.formatErrors));
    }
    put(path: string, body: Object = {}): Observable<any> {
        return this.http.put(`${environment.IXFI_API}${path}`, body).pipe(catchError(this.formatErrors));
    }

    post(path: string, body: Object = {}): Observable<any> {
        return this.http.post(`${environment.IXFI_API}${path}`, body).pipe(catchError(this.formatErrors));
    }
}
